import { provideHttpClient } from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  isDevMode,
  LOCALE_ID,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import {
  provideClientHydration,
  withEventReplay,
} from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideRouter,
  Router,
  withComponentInputBinding,
} from '@angular/router';
import { IconDefinition } from '@ant-design/icons-angular';
import {
  BankOutline,
  BellOutline,
  BookOutline,
  CalendarOutline,
  CheckCircleOutline,
  CloseCircleFill,
  CloseCircleOutline,
  CloseOutline,
  CoffeeOutline,
  CommentOutline,
  DeleteFill,
  DeleteOutline,
  DownloadOutline,
  EditFill,
  ExclamationCircleOutline,
  EyeInvisibleOutline,
  EyeOutline,
  FileAddOutline,
  FormOutline,
  GlobalOutline,
  HeartOutline,
  HomeOutline,
  InboxOutline,
  InfoCircleOutline,
  LeftOutline,
  LockFill,
  LockOutline,
  MailOutline,
  MessageOutline,
  MinusOutline,
  PlayCircleFill,
  PlayCircleOutline,
  CheckCircleFill,
  PlusOutline,
  ReadOutline,
  ReconciliationOutline,
  RightOutline,
  RightCircleOutline,
  SafetyOutline,
  SaveOutline,
  SearchOutline,
  ShoppingCartOutline,
  CheckOutline,
  SwapOutline,
  TrophyOutline,
  TrophyFill,
  ExportOutline,
  StopOutline,
  UserOutline,
  SketchOutline,
  LogoutOutline,
  LoginOutline,
  MenuOutline,
  ClockCircleOutline,
  GiftOutline,
  SendOutline,
  UsergroupAddOutline,
  ArrowLeftOutline,
  EditOutline,
  MoreOutline,
  PlusCircleOutline,
  ReloadOutline,
  GiftFill,
  TrophyTwoTone,
} from '@ant-design/icons-angular/icons';
import { createErrorHandler, TraceService } from '@sentry/angular';
import { NZ_DATE_CONFIG, NZ_DATE_LOCALE, NZ_I18N } from 'ng-zorro-antd/i18n';
import { provideNzIcons } from 'ng-zorro-antd/icon';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { FIREBASE_CONFIG_DEV, FIREBASE_CONFIG_PROD } from '@ess/shared-utils';
import { provideGraphQL } from '../graphql/graphql.options';
import { TRANSLATION } from '../i18n';
import { LocaleService } from '../i18n/locale.service';
import { appRoutes } from './app.routes';

const icons: IconDefinition[] = [
  BankOutline,
  BellOutline,
  BookOutline,
  CalendarOutline,
  CheckCircleOutline,
  CloseCircleFill,
  CloseCircleOutline,
  CloseOutline,
  CoffeeOutline,
  CommentOutline,
  DeleteFill,
  DeleteOutline,
  DownloadOutline,
  EditFill,
  ExclamationCircleOutline,
  EyeInvisibleOutline,
  EyeOutline,
  FileAddOutline,
  FormOutline,
  GlobalOutline,
  HeartOutline,
  HomeOutline,
  InboxOutline,
  InfoCircleOutline,
  LeftOutline,
  MailOutline,
  MessageOutline,
  MinusOutline,
  PlayCircleOutline,
  PlusOutline,
  ReadOutline,
  ReconciliationOutline,
  RightOutline,
  RightCircleOutline,
  SafetyOutline,
  SaveOutline,
  SearchOutline,
  ShoppingCartOutline,
  CheckOutline,
  CloseOutline,
  CalendarOutline,
  LockOutline,
  LockFill,
  CheckCircleOutline,
  CheckCircleFill,
  PlayCircleFill,
  MessageOutline,
  LeftOutline,
  RightOutline,
  SwapOutline,
  TrophyOutline,
  TrophyFill,
  ExportOutline,
  StopOutline,
  UserOutline,
  SketchOutline,
  LogoutOutline,
  LoginOutline,
  MenuOutline,
  ClockCircleOutline,
  GiftOutline,
  SendOutline,
  UsergroupAddOutline,
  ArrowLeftOutline,
  EditOutline,
  MoreOutline,
  PlusCircleOutline,
  ReloadOutline,
  GiftFill,
  TrophyTwoTone,
];

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppInitializer(() => {
      return inject(LocaleService).loadLocaleConfig();
    }),
    {
      provide: ErrorHandler,
      useValue: createErrorHandler(),
    },
    {
      provide: TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(TraceService);
    }),
    provideHttpClient(),
    provideGraphQL(),
    provideClientHydration(withEventReplay()),
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(appRoutes, withComponentInputBinding()),
    provideAnimationsAsync(),
    provideFirebaseApp(() =>
      initializeApp(isDevMode() ? FIREBASE_CONFIG_DEV : FIREBASE_CONFIG_PROD),
    ),
    provideAuth(() => getAuth()),
    provideNzIcons(icons),
    importProvidersFrom(NzModalModule),
    {
      provide: LOCALE_ID,
      deps: [LocaleService],
      useFactory: (localeService: LocaleService) =>
        localeService.translation.langCode,
    },
    {
      provide: NZ_I18N,
      deps: [LocaleService],
      useFactory: (localeService: LocaleService) => localeService.nzLocale,
    },
    {
      provide: NZ_DATE_CONFIG,
      deps: [LocaleService],
      useFactory: (localeService: LocaleService) =>
        localeService.firstDayOfWeek,
    },
    {
      provide: NZ_DATE_LOCALE,
      deps: [LocaleService],
      useFactory: (localeService: LocaleService) => localeService.locale,
    },
    {
      provide: TRANSLATION,
      deps: [LocaleService],
      useFactory: (localeService: LocaleService) => localeService.translation,
    },
  ],
};
